import { timezoneStore } from "@/api/hooks/useConfig";

import { snakeCase } from "lodash";
import moment from "moment";
import { FilterTimeFrameValue } from "server/config-api/index.js";
import { match } from "ts-pattern";

export type CardFilterTimeFrameIso = {
  startTimeISO: string;
  endTimeISO: string;
};

/** Generates a timeframe object for DWH api request */
export const getTimeFrame = (
  timeFrame: FilterTimeFrameValue,
  defaultTimeFrame?: FilterTimeFrameValue,
): CardFilterTimeFrameIso => {
  const { timezone } = timezoneStore.getState();
  const normalisedTimeframe = snakeCase(
    timeFrame,
  ).toUpperCase() as FilterTimeFrameValue;

  const endTimeToday = moment().tz(timezone, true).endOf("day").toISOString();

  // If the input timeframe is not valid, use the default timeframe
  if (
    defaultTimeFrame &&
    !Object.values(FilterTimeFrameValue).includes(normalisedTimeframe)
  ) {
    return getTimeFrame(defaultTimeFrame);
  }

  return match(normalisedTimeframe)
    .with(FilterTimeFrameValue.Today, () => ({
      startTimeISO: moment().tz(timezone, true).startOf("day").toISOString(),
      endTimeISO: endTimeToday,
    }))
    .with(FilterTimeFrameValue.Yesterday, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .subtract(1, "days")
        .startOf("day")
        .toISOString(),
      endTimeISO: moment()
        .tz(timezone, true)
        .subtract(1, "days")
        .endOf("day")
        .toISOString(),
    }))
    .with(FilterTimeFrameValue.LastWeek, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .subtract(1, "week")
        .startOf("day")
        .toISOString(),
      endTimeISO: endTimeToday,
    }))
    .with(FilterTimeFrameValue.Last30Days, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .subtract(30, "days")
        .startOf("day")
        .toISOString(),
      endTimeISO: endTimeToday,
    }))
    .with(FilterTimeFrameValue.Last90Days, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .subtract(90, "days")
        .startOf("day")
        .toISOString(),
      endTimeISO: endTimeToday,
    }))
    .with(FilterTimeFrameValue.Last3Months, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .subtract(3, "month")
        .startOf("month")
        .toISOString(),
      endTimeISO: endTimeToday,
    }))
    .with(FilterTimeFrameValue.Last6Months, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .subtract(6, "month")
        .startOf("month")
        .toISOString(),
      endTimeISO: endTimeToday,
    }))
    .with(FilterTimeFrameValue.Last12Months, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .subtract(12, "month")
        .startOf("month")
        .toISOString(),
      endTimeISO: endTimeToday,
    }))
    .with(FilterTimeFrameValue.LastMonth, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .subtract(1, "month")
        .startOf("month")
        .toISOString(),
      endTimeISO: moment()
        .tz(timezone, true)
        .subtract(1, "month")
        .endOf("month")
        .toISOString(),
    }))
    .with(FilterTimeFrameValue.LastYear, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .subtract(1, "year")
        .startOf("year")
        .toISOString(),
      endTimeISO: moment()
        .tz(timezone, true)
        .subtract(1, "year")
        .endOf("year")
        .toISOString(),
    }))
    .with(FilterTimeFrameValue.YearToDate, () => ({
      startTimeISO: moment().tz(timezone, true).startOf("year").toISOString(),
      endTimeISO: endTimeToday,
    }))
    .with(FilterTimeFrameValue.Last6FullMonths, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .subtract(6, "month")
        .startOf("month")
        .toISOString(),
      endTimeISO: moment()
        .tz(timezone, true)
        .subtract(1, "month")
        .endOf("month")
        .toISOString(),
    }))
    .with(FilterTimeFrameValue.LastFullYear, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .subtract(12, "month")
        .startOf("month")
        .toISOString(),
      endTimeISO: moment()
        .tz(timezone, true)
        .subtract(1, "month")
        .endOf("month")
        .toISOString(),
    }))
    .with(FilterTimeFrameValue.SinceBeginning, () => ({
      startTimeISO: "2017-01-01T00:00:00Z", // Carrot (WasteIQ) was founded in 2017
      endTimeISO: endTimeToday,
    }))
    .with(FilterTimeFrameValue.ThisMonth, () => ({
      startTimeISO: moment().tz(timezone, true).startOf("month").toISOString(),
      endTimeISO: endTimeToday,
    }))
    .with(FilterTimeFrameValue.ThisQuarter, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .startOf("quarter")
        .toISOString(),
      endTimeISO: endTimeToday,
    }))
    .with(FilterTimeFrameValue.PrevQuarter, () => ({
      startTimeISO: moment()
        .tz(timezone, true)
        .subtract(1, "quarter")
        .startOf("quarter")
        .toISOString(),
      endTimeISO: moment()
        .tz(timezone, true)
        .subtract(1, "quarter")
        .endOf("quarter")
        .toISOString(),
    }))
    .exhaustive();
};

export type TimestampISO =
  `${number}-${number}-${number}T${number}:${number}:${number}.${number}Z`;
export type TimeFilterCustomDate = `${TimestampISO}&${TimestampISO}`;

export const getCustomTimeFrame = (
  customTimeframe: TimeFilterCustomDate,
): CardFilterTimeFrameIso => {
  const [startTimeISO = "", endTimeISO = ""] = customTimeframe.split("&") as [
    TimestampISO,
    TimestampISO,
  ];

  return { startTimeISO, endTimeISO };
};

export enum Periodicity {
  LAST_6_COMPLETED_MONTHS = "last6CompletedMonths",
  LAST_12_COMPLETED_MONTHS = "last12CompletedMonths",
}

export const getPeriodicity = (currentStartTime: string) => {
  if (
    currentStartTime ===
    getTimeFrame(FilterTimeFrameValue.Last6FullMonths).startTimeISO
  ) {
    return Periodicity.LAST_6_COMPLETED_MONTHS;
  }
  if (
    currentStartTime ===
    getTimeFrame(FilterTimeFrameValue.LastFullYear).startTimeISO
  ) {
    return Periodicity.LAST_12_COMPLETED_MONTHS;
  }

  return Periodicity.LAST_6_COMPLETED_MONTHS;
};
